/**
 * Handles overlay config loading, chat-ready signals, auto-open,
 * and layout persistence for the chat iframe.
 * Extracted from IframeManager to keep the class focused on lifecycle.
 */
import type { OverlayLayout, WidgetDimensions, WidgetState, BackendOverlayConfig } from '../types';
import { MessageTypes } from '../types';
import type { MessageBridge } from './MessageBridge';

/**
 * Mutable state owned by IframeManager, passed by reference so handler
 * mutations are visible without manual write-back.
 */
export interface ConfigMutableState {
  backendOverlayConfig: BackendOverlayConfig | null;
  isChatInterfaceReady: boolean;
  isOpeningFromPersistState: boolean;
  autoOpenTimeout: ReturnType<typeof setTimeout> | null;
  actualBackendDimensions?: { width: number; height: number };
}

export interface IframeConfigContext {
  iframe: HTMLIFrameElement | null;
  layout: OverlayLayout;
  messageBridge: MessageBridge | null;
  configState: ConfigMutableState;
  debug: boolean;

  // Callbacks into IframeManager
  makeVisible: () => void;
  getResponsiveDimensions: (state: WidgetState) => WidgetDimensions;
  resizeIframe: (dimensions: WidgetDimensions, state: WidgetState, animated: boolean) => void;
  notifyStateChange: () => void;
  updateState: (partial: { position?: string; dimensions?: { width: number; height: number } }) => void;
  updateLayout: (partial: Partial<OverlayLayout>) => void;
}

/**
 * Handle overlay config loaded message from ChatInterface
 */
export function handleOverlayConfigLoaded(ctx: IframeConfigContext, message: any): void {
  if (!message.data || !message.data.config) {
    console.error('[IframeManager] Invalid overlay config message:', message);
    return;
  }

  // Save the backend config for reference
  ctx.configState.backendOverlayConfig = message.data.config;

  if (ctx.debug) {
    console.log('[IframeManager] Received overlay config:', {
      source: message.data.source,
      overlayName: message.data.overlayName,
      config: ctx.configState.backendOverlayConfig
    });
  }

  // Apply iframe-specific overrides to config
  const iframeOverrides = {
    layout: {
      launcherOffset: { x: 0, y: 0 },
      margin: 0,
      alignment: '0 0 0 0px'
    },
    behavior: {
      autoOpen: false,
    }
  };

  // Apply layout properties from backend config to iframe element
  applyBackendLayoutToIframe(ctx);

  // Handle layout persistence BEFORE auto-open (persistState has priority)
  handleLayoutPersistence(ctx);

  // Handle auto-open behavior based on backend config
  handleAutoOpen(ctx);

  // Send ready signal to ChatInterface
  if (ctx.messageBridge) {
    ctx.messageBridge.send(MessageTypes.IFRAME_READY_TO_DISPLAY, {
      finalConfig: {
        ...ctx.configState.backendOverlayConfig,
        ...iframeOverrides
      }
    });
  }

  // Don't make iframe visible yet - wait for CHAT_INTERFACE_READY message
  checkAndMakeVisible(ctx);
}

/**
 * Handle ChatInterface ready message
 */
export function handleChatInterfaceReady(ctx: IframeConfigContext, _message: any): void {
  if (ctx.debug) console.log('[IframeManager] ChatInterface is fully initialized and ready');
  ctx.configState.isChatInterfaceReady = true;
  checkAndMakeVisible(ctx);
}

/**
 * Check if both conditions are met and make iframe visible
 */
function checkAndMakeVisible(ctx: IframeConfigContext): void {
  if (ctx.configState.backendOverlayConfig && ctx.configState.isChatInterfaceReady) {
    if (ctx.debug) console.log('[IframeManager] Both conditions met - making iframe visible');
    ctx.makeVisible();
  }
}

/**
 * Apply relevant layout properties from backend config to iframe element
 */
function applyBackendLayoutToIframe(ctx: IframeConfigContext): void {
  if (!ctx.iframe || !ctx.configState.backendOverlayConfig?.layout) return;

  const layout = ctx.configState.backendOverlayConfig.layout;

  // Update iframe positioning based on backend config
  if (layout.position) {
    ctx.updateLayout({ position: layout.position });
  }

  // Update dimensions if provided
  if (layout.width || layout.height) {
    ctx.updateLayout({
      width: layout.width || ctx.layout.width,
      height: layout.height || ctx.layout.height,
    });

    // Store actual backend dimensions for responsive calculations
    ctx.configState.actualBackendDimensions = {
      width: typeof layout.width === 'number' ? layout.width : parseInt(String(layout.width)) || 400,
      height: typeof layout.height === 'number' ? layout.height : parseInt(String(layout.height)) || 600
    };
  }

  // Apply other layout properties
  if (layout.zIndex) {
    ctx.updateLayout({ zIndex: layout.zIndex });
  }

  if (layout.borderRadius !== undefined) {
    ctx.updateLayout({ borderRadius: layout.borderRadius });
  }

  if (layout.shadow !== undefined) {
    ctx.updateLayout({ shadow: layout.shadow });
  }

  // Update iframe state
  ctx.updateState({
    position: ctx.layout.position || 'bottom-right',
    dimensions: {
      width: ctx.layout.width || 400,
      height: ctx.layout.height || 600,
    }
  });

  // Only reset to launcher on first initialization.
  // On re-initialization (e.g., agent change triggers new overlay-config-loaded),
  // the widget is already in the correct state — just update layout properties.
  if (!ctx.configState.isChatInterfaceReady) {
    const dimensions = ctx.getResponsiveDimensions('launcher');
    ctx.resizeIframe(dimensions, 'launcher', false);
  }
  ctx.notifyStateChange();
}

/**
 * Handle auto-open based on backend config
 */
function handleAutoOpen(ctx: IframeConfigContext): void {
  // Skip if already opening from persistState
  if (ctx.configState.isOpeningFromPersistState) {
    if (ctx.debug) console.log('[IframeManager] Skipping auto-open: widget already being restored from persistState');
    return;
  }

  // Check if auto-open is enabled in backend config
  if (!ctx.configState.backendOverlayConfig?.behavior?.autoOpen) {
    if (ctx.debug) console.log('[IframeManager] Auto-open disabled or not configured');
    return;
  }

  // Get the delay from backend config, default to 0 if not specified
  const delay = ctx.configState.backendOverlayConfig.behavior.autoOpenDelay || 0;

  if (ctx.debug) console.log('[IframeManager] Scheduling auto-open with delay:', delay);

  // Clear any existing timeout
  if (ctx.configState.autoOpenTimeout) {
    clearTimeout(ctx.configState.autoOpenTimeout);
  }

  // Schedule auto-open after the specified delay
  ctx.configState.autoOpenTimeout = setTimeout(() => {
    if (ctx.debug) console.log('[IframeManager] Auto-opening widget after delay');

    // Send message to ChatInterface to show the widget
    if (ctx.messageBridge) {
      ctx.messageBridge.send(MessageTypes.SHOW_WIDGET, {
        source: 'auto-open',
        delay: delay
      });
    }

    ctx.configState.autoOpenTimeout = null;
  }, delay);
}

/**
 * Handle layout persistence based on backend config
 * Checks localStorage for previous UI state and restores if needed
 */
export function handleLayoutPersistence(ctx: IframeConfigContext): void {
  // Check if layout.persistState is enabled in backend config
  if (!ctx.configState.backendOverlayConfig?.layout?.persistState) {
    if (ctx.debug) console.log('[IframeManager] Layout persistState disabled or not configured');
    return;
  }

  // Read stored UI state from localStorage
  const stored = localStorage.getItem('kaigentic-chat-overlay-state');
  if (!stored) {
    if (ctx.debug) console.log('[IframeManager] No stored overlay state found');
    return;
  }

  try {
    const state = JSON.parse(stored);
    if (ctx.debug) console.log('[IframeManager] Found stored overlay state:', state);

    // Persisted state exists — it takes priority over auto-open regardless of isOpen value.
    // This prevents auto-open from overriding the user's explicit close/minimize action.
    ctx.configState.isOpeningFromPersistState = true;

    // Clear any pending autoOpen timeout since persistState takes priority
    if (ctx.configState.autoOpenTimeout) {
      clearTimeout(ctx.configState.autoOpenTimeout);
      ctx.configState.autoOpenTimeout = null;
      if (ctx.debug) console.log('[IframeManager] Cancelled autoOpen due to persistState');
    }

    // Handle different stored states
    if (state.isOpen) {
      if (state.isMinimized) {
        // Widget was open but minimized
        if (ctx.debug) console.log('[IframeManager] Restoring widget to minimized state from persistState');

        // Resize iframe on the host page to minimized dimensions
        const minimizedDimensions = ctx.getResponsiveDimensions('minimized');
        ctx.resizeIframe(minimizedDimensions, 'minimized', false);

        if (ctx.messageBridge) {
          ctx.messageBridge.send(MessageTypes.MINIMIZE_WIDGET, {
            source: 'persist-state',
            previousState: state
          });
        }
      } else {
        // Widget was open and expanded
        if (ctx.debug) console.log('[IframeManager] Restoring widget to open state from persistState');

        // Resize iframe on the host page to expanded dimensions
        const expandedDimensions = ctx.getResponsiveDimensions('expanded');
        ctx.resizeIframe(expandedDimensions, 'expanded', false);

        if (ctx.messageBridge) {
          ctx.messageBridge.send(MessageTypes.SHOW_WIDGET, {
            source: 'persist-state',
            previousState: state
          });
        }
      }
    } else {
      // Widget was closed — keep it closed (launcher state)
      if (ctx.debug) console.log('[IframeManager] Persisted state is closed, keeping widget as launcher');
    }
  } catch (e) {
    console.warn('[IframeManager] Failed to parse stored overlay state:', e);
  }
}
